import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./withdraws.module.scss";
import {
  BUTTON,
  HEADER,
  PENDING_WITHDRAWS_TAB,
  TABLE_HEADERS,
  WITHDRAW_STATUS,
} from "./constants";
import SearchInput from "../../components/common/searchInput";
import { Tab, Tabs } from "@mui/material";
import WithdrawsTableShimmer from "./WithdrawsTableShimmer";
import { getWithdraws } from "../../setup/api/services/withdraws";
import WithdrawsListItem from "./WithdrawsListItem";
import Paginations from "../../components/common/Pagination";
import { IWithdrawBody } from "../../setup/api/services/withdraws/models";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;

const Withdraws = () => {
  const userIdTimeoutRef = useRef<NodeJS.Timer>();
  const transactionIdTimeoutRef = useRef<NodeJS.Timer>();
  const [withdraws, setWithdraws] = useState<IWithdrawBody[]>();
  const [userIdSearchValue, setUserIdSearchValue] = useState("");
  const [transactionIdSearchValue, setTransactionIdSearchValue] = useState("");
  const [tabType, setTabType] = useState<WITHDRAW_STATUS>(
    WITHDRAW_STATUS.IN_PROGRESS
  );
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const fetchWithdraws = useCallback(
    async (beneficiaryId?: string, domainId?: string) => {
      setLoading(true);
      try {
        const body = {
          beneficiaryId,
          domainId,
          statuses:
            tabType === WITHDRAW_STATUS?.COMPLETED
              ? [tabType]
              : [
                  ...Object.values(WITHDRAW_STATUS).filter(
                    (status) => status !== WITHDRAW_STATUS?.COMPLETED
                  ),
                ],
          page: page - 1,
          size: PAGE_SIZE,
        };
        const response = await getWithdraws(body);
        setWithdraws(response.data.content);
        setPagesCount(response.data.totalPages);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setWithdraws([]);
      }
    },
    [page, tabType]
  );

  const handleUserIdSearchValue = useCallback(
    (event) => {
      clearTimeout(userIdTimeoutRef.current);
      const value = event.target.value;
      setUserIdSearchValue(value);
      // Clear transaction ID when searching by user ID
      setTransactionIdSearchValue("");
      userIdTimeoutRef.current = setTimeout(() => {
        fetchWithdraws(value, "");
      }, 300);
    },
    [fetchWithdraws]
  );

  const handleTransactionIdSearchValue = useCallback(
    (event) => {
      clearTimeout(transactionIdTimeoutRef.current);
      const value = event.target.value;
      setTransactionIdSearchValue(value);
      // Clear user ID when searching by transaction ID
      setUserIdSearchValue("");
      transactionIdTimeoutRef.current = setTimeout(() => {
        fetchWithdraws("", value);
      }, 300);
    },
    [fetchWithdraws]
  );

  useEffect(() => {
    fetchWithdraws();
  }, [tabType, page, fetchWithdraws]);

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: WITHDRAW_STATUS
  ) => {
    setTabType(newValue);
    setPage(1);
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  return (
    <div className={styles["page_layout"]}>
      <h1>{HEADER}</h1>
      <div className={styles.header_container}>
        <div className={styles["right_section"]}>
          <Tabs
            style={{ alignItems: "center" }}
            className={styles.tab_wrapper}
            value={tabType}
            onChange={handleTabChange}>
            <Tab
              value={WITHDRAW_STATUS.IN_PROGRESS}
              label={PENDING_WITHDRAWS_TAB}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
          </Tabs>
        </div>
        <div className={styles["left_section"]}>
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_user_id")}
            value={userIdSearchValue}
            style={{ height: "35px", width: "252px", marginLeft: "10px" }}
            handleChange={handleUserIdSearchValue}
          />
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_transaction_id")}
            value={transactionIdSearchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={handleTransactionIdSearchValue}
          />
        </div>
      </div>

      <table className={styles.table}>
        <tr className={styles.table_header}>
          <th className={styles.hint}>{TABLE_HEADERS.USER_NAME}</th>
          <th className={styles.hint}>{TABLE_HEADERS.REQUEST_TIME}</th>
          <th className={styles.hint}>{TABLE_HEADERS.AMOUNT}</th>
          <th className={styles.hint}>{TABLE_HEADERS.STATUS}</th>
        </tr>
        {loading ? (
          <WithdrawsTableShimmer />
        ) : withdraws?.length === 0 ? (
          <div className={styles.empty_list_container}>
            <p>{`لا يوجد طلبات سحب في ${
              tabType === WITHDRAW_STATUS.IN_PROGRESS
                ? "إنتظار التحويل"
                : "تم التحويل"
            }`}</p>
          </div>
        ) : (
          <tbody className={styles.table_body}>
            {withdraws?.map((request, index) => (
              <WithdrawsListItem key={index} request={request} />
            ))}
          </tbody>
        )}
      </table>

      <>
        <div className={withdraws?.length ? "paginatorContainer" : "hide"}>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};

export default Withdraws;
